import type { CSSProperties } from 'react';
import type { EggAppearance } from '../lib/types';

/**
 * One egg component renders every family.
 *
 * A family supplies three colours and a pattern name, and the shell is built
 * from layered gradients driven by CSS custom properties. That is what makes
 * "you eventually recognise a family by sight" affordable — adding a 40th egg
 * is a seed row, not another hand-drawn asset.
 */

export type EggSize = 'xs' | 'sm' | 'md' | 'lg';
export type EggState = 'idle' | 'ready' | 'still' | 'hatching';

const UNKNOWN: EggAppearance = {
  shellBase: '#3a4450',
  shellAccent: '#232a33',
  shellDetail: '#5d6875',
  pattern: 'speckle',
};

export function EggArt({
  appearance,
  size = 'md',
  state = 'idle',
  label,
}: {
  /** null renders the "never seen one of these" silhouette. */
  appearance: EggAppearance | null;
  size?: EggSize;
  state?: EggState;
  label?: string;
}) {
  const look = appearance ?? UNKNOWN;

  const stateClass =
    state === 'ready'
      ? 'egg--ready'
      : state === 'hatching'
        ? 'hatch-egg'
        : state === 'idle'
          ? 'egg--idle'
          : '';

  const style = {
    '--shell-base': look.shellBase,
    '--shell-accent': look.shellAccent,
    '--shell-detail': look.shellDetail,
  } as CSSProperties;

  return (
    <div
      className={`egg2 egg2--${size} egg2--${look.pattern} ${stateClass} ${
        appearance ? '' : 'egg2--unknown'
      }`}
      style={style}
      role="img"
      aria-label={label ?? (appearance ? 'Egg' : 'An egg you have never seen')}
    >
      <span className="egg2__pattern" aria-hidden />
      <span className="egg2__shine" aria-hidden />
    </div>
  );
}
